import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui';

/**
 * SuperAdminNotFound — the super-admin-nested 404 (PR-43).
 *
 * Sibling of SettingsNotFound (PR-39). Rendered by
 * `<Route path="*" element={<SuperAdminNotFound />} />` inside
 * `<Route path="super-admin" element={<SuperAdminGuard />}>`.
 *
 * `SuperAdminGuard` renders `<Outlet />`, so this component displays inside
 * the super-admin content column and the top-level nav stays intact.
 *
 * The affordance goes to `/super-admin/dealers` — the super-admin landing
 * (`index` route already redirects there). A super-admin who typo'd a
 * sub-path was clearly trying to be in super-admin; send them back to
 * where they started, not to the app dashboard.
 */
export default function SuperAdminNotFound() {
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="text-center max-w-md px-6">
        <MagnifyingGlassIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <p className="text-6xl font-bold text-primary-600 mb-2">404</p>
        <h1 style={{ font: 'var(--text-h1)', color: 'var(--color-text-primary)', marginBottom: '0.5rem' }}>
          Super-admin page not found
        </h1>
        <p className="text-gray-500 mb-2">
          We couldn&apos;t find a super-admin page at{' '}
          <code style={{ color: 'var(--color-text-primary)' }}>{location.pathname}</code>.
        </p>
        <p className="text-gray-500 mb-6">
          Pick something from the list on the left, or head back to Contractors.
        </p>
        <Button onClick={() => navigate('/super-admin/dealers')}>
          Go to Contractors
        </Button>
      </div>
    </div>
  );
}
